/**
 * RecordingBarContainer — supplies layout hooks (safe-area inset) to RecordingBarView.
 *
 * Reads bottom safe-area inset so the sticky bottom bar avoids overlap with
 * the iOS home indicator, Android gesture bar, and Safari bottom chrome (SE-4618).
 * @module features/voice-report/components/RecordingBar/RecordingBarContainer
 */

import React, { memo } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import RecordingBarView from "./RecordingBarView";

/**
 * Props for RecordingBarContainer
 */
interface RecordingBarContainerProps {
  /** Elapsed recording time in seconds */
  readonly elapsedSeconds: number;
  /** Live transcript from speech recognition (kept for hook compatibility) */
  readonly liveTranscript: string;
  /** Real-time frequency levels from Web Audio API (0-1 range) */
  readonly audioLevels: readonly number[];
  /** Called when Cancel is pressed */
  readonly onCancel: () => void;
  /** Called when Finish is pressed */
  readonly onFinish: () => void;
}

/**
 * Container that supplies the bottom safe-area inset to RecordingBarView.
 * @param props - View props (forwarded as-is)
 * @returns RecordingBarView with bottomSafeArea wired
 */
const RecordingBarContainer = (props: RecordingBarContainerProps) => {
  const { bottom: bottomInset } = useSafeAreaInsets();
  return <RecordingBarView {...props} bottomSafeArea={bottomInset} />;
};

RecordingBarContainer.displayName = "RecordingBarContainer";

export default memo(RecordingBarContainer);
